// @mui
import { styled, keyframes } from '@mui/material/styles';
import { Typography, Box, Link } from '@mui/material';
// components
import Logo from '../components/Logo';
import Page from '../components/Page';
// sections
import { RegisterForm } from '../sections/auth/register';

// ----------------------------------------------------------------------

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateX(20px); }
  to   { opacity: 1; transform: translateX(0); }
`;

// ─── Root ────────────────────────────────────────────────────────────────────
const RootStyle = styled('div')({
  minHeight: '100vh',
  display: 'flex',
  position: 'relative',
  overflow: 'hidden',
  background: '#ffffff',
});

// ─── Left panel ──────────────────────────────────────────────────────────────
const LeftPanel = styled('div')(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  background: '#f5f5f7',
  position: 'relative',
  padding: theme.spacing(3),
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-30%',
    left: '-10%',
    width: '60%',
    height: '70%',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(0,122,255,0.07) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-20%',
    right: '-10%',
    width: '55%',
    height: '65%',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(100,210,255,0.08) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
}));

const FormContainer = styled('div')({
  width: '100%',
  maxWidth: 420,
  animation: `${fadeUp} 0.7s cubic-bezier(0.22, 1, 0.36, 1) both`,
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100%',
  flexGrow: 1,
});

const FormContent = styled('div')({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
});

const FooterTypography = styled(Typography)({
  fontSize: '0.8rem',
  color: '#86868b',
  textAlign: 'center',
  marginTop: 'auto',
  paddingBottom: '24px',
});

// ─── Right panel ─────────────────────────────────────────────────────────────
const RightPanel = styled('div')(({ theme }) => ({
  flex: 1,
  display: 'none',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  padding: '0 8%',
  background: '#ffffff',
  position: 'relative',
  [theme.breakpoints.up('md')]: { display: 'flex' },
}));

const RightContent = styled('div')({
  maxWidth: 540,
  animation: `${fadeIn} 0.8s cubic-bezier(0.22, 1, 0.36, 1) both`,
});

const GradientText = styled('span')({
  background: 'linear-gradient(90deg, #5E5CE6 0%, #0A84FF 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
});

const FeatureItem = styled('div')(({ delay = 0 }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  marginBottom: 24,
  animation: `${fadeIn} 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s both`,
}));

const CheckCircle = styled('div')({
  width: 20,
  height: 20,
  borderRadius: 6,
  background: 'rgba(94, 92, 230, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  '& svg': {
    width: 10,
    height: 10,
    fill: 'none',
    stroke: '#5E5CE6',
    strokeWidth: 3,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  },
});

const LogosContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 32,
  flexWrap: 'wrap',
  marginTop: 16,
});

const LogoText = styled(Typography)({
  color: '#86868b',
  fontWeight: 600,
  fontSize: '1.25rem',
  letterSpacing: '-0.03em',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

// ─── Data ────────────────────────────────────────────────────────────────────
const FEATURES = [
  { delay: 0.2, text: 'Create powerful bill payment apps with ease.' },
  { delay: 0.4, text: 'Convert any website into a mobile app seamlessly.' },
  { delay: 0.6, text: 'Launch custom apps for Android, iOS, and Web in minutes.' },
];

// ─── Component ───────────────────────────────────────────────────────────────
export default function Register() {
  return (
    <Page title="Register">
      <RootStyle>
        {/* ── Left: form ── */}
        <LeftPanel>
          <FormContainer>
            <FormContent>
              <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                <Logo sx={{ width: '130px', height: '70px' }} />
              </Box>

              <Typography
                variant="h4"
                sx={{
                  fontFamily: '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Helvetica Neue", sans-serif',
                  fontWeight: 600,
                  fontSize: '1.75rem',
                  color: '#1d1d1f',
                  lineHeight: 1.2,
                  mb: 5,
                  textAlign: 'center',
                }}
              >
                Sign up to Alis Build
              </Typography>

              <RegisterForm />
            </FormContent>

            <FooterTypography>
              By creating an account, you agree to the{' '}
              <Link href="https://alisbuild.com/term-of-service" underline="hover" color="inherit">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link href="https://alisbuild.com/privacy-policy" underline="hover" color="inherit">
                Privacy Policy
              </Link>
            </FooterTypography>
          </FormContainer>
        </LeftPanel>

        {/* ── Right: promo ── */}
        <RightPanel>
          <RightContent>
            {/* Headline */}
            <Typography
              variant="h2"
              sx={{
                fontFamily: '"SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif',
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.5rem' },
                lineHeight: 1.2,
                mb: 5,
                color: '#1d1d1f',
                letterSpacing: '-0.02em',
              }}
            >
              Build Powerful <GradientText>Mobile Apps</GradientText> Without Coding.
            </Typography>

            {/* Feature list */}
            <Box sx={{ display: 'flex', flexDirection: 'column', mb: 8 }}>
              {FEATURES.map((f) => (
                <FeatureItem key={f.text} delay={f.delay}>
                  <CheckCircle>
                    <svg viewBox="0 0 12 12">
                      <polyline points="2,6 5,9 10,3" />
                    </svg>
                  </CheckCircle>
                  <Typography
                    sx={{
                      color: '#515154',
                      fontSize: '1.125rem',
                      fontWeight: 400,
                      lineHeight: 1.5,
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {f.text}
                  </Typography>
                </FeatureItem>
              ))}
            </Box>

            {/* Trusted by */}
            <Typography
              sx={{
                color: '#5E5CE6',
                fontSize: '0.875rem',
                fontWeight: 500,
                mb: 1,
                backgroundColor: 'rgba(94, 92, 230, 0.08)',
                padding: '6px 14px',
                borderRadius: '8px',
                display: 'inline-block',
              }}
            >
              Trusted by industry leading companies
            </Typography>

            <LogosContainer>
              <LogoText sx={{ animation: `${fadeIn} 0.6s both 0.8s` }}>OpenAI</LogoText>
              <LogoText sx={{ animation: `${fadeIn} 0.6s both 0.9s` }}>ANTHROP\C</LogoText>
              <LogoText sx={{ animation: `${fadeIn} 0.6s both 1.0s` }}>▲ Vercel</LogoText>
              <LogoText sx={{ animation: `${fadeIn} 0.6s both 1.1s` }}>CURSOR</LogoText>
              <LogoText sx={{ animation: `${fadeIn} 0.6s both 1.2s` }}>perplexity</LogoText>
            </LogosContainer>
          </RightContent>
        </RightPanel>
      </RootStyle>
    </Page>
  );
}
